import { DuplicateButton } from '@/components/admin/duplicate-button';
import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { DirectionField } from '@/components/admin/protocoles/step/direction-field';
import { conditionDuplicationBlocked, duplicateCondition } from '@/lib/duplication';
import {
  ACTION_KINDS,
  ACTION_LABEL,
  COUNTER_TRIGGER_LABEL,
  OBJECTIVE_CLASS_LABEL,
  objectiveTitle,
  stepTitle,
} from '@/lib/protocol-labels';
import type { ActionContext } from '@/lib/protocol-step-draft';
import {
  addAction,
  addGroup,
  addTrigger,
  allObjectives,
  availableTriggerObjectives,
  moveCondition,
  newCondition,
  otherSteps,
  removeAction,
  removeCondition,
  removeTrigger,
  replaceCondition,
  setActionKind,
  setActionParam,
  setTrigger,
  setTriggerOutcome,
  triggerValue,
} from '@/lib/protocol-step-draft';
import type {
  CriterionCard,
  ExitAction,
  ExitActionKind,
  ExitCondition,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import {
  PROTOCOL_CLOSE_TEXT_MAX,
  PROTOCOL_CONDITIONS_MAX,
  PROTOCOL_DEFAULT_CLOSE_TEXT,
  isTerminalAction,
  objectiveOutcomeSchema,
} from '@matter_extractor/shared';
import { Icon, Select, TextField, Tooltip } from '@pms-digital/design-system';
import type { DragEvent as ReactDragEvent } from 'react';
import { useState } from 'react';

/**
 * The exit conditions of a step: an ORDERED list, evaluated after the
 * garde-fous, the first true one winning (refonte modulaire §8.4; maquette
 * `conditionHTML`).
 *
 * They deviate the implicit logic, they do not replace it - and a condition
 * that fires puts the implicit logic to sleep for that turn even when none of
 * its actions is terminal. Which is why the card is drawn as a sentence and not
 * as a form: « quand A et B ou C → invalider X puis aller à 3 · Contexte » is
 * how §8.4 words it, and the selects sit inside that sentence.
 *
 * ## The order is data, so it is draggable and numbered
 *
 * « la première vraie gagne » makes the rank of a card its priority. The number
 * is therefore on the card and not merely implied by its position, and the
 * gesture that changes it is a drag, like the frise's.
 *
 * ## « et » groups, « ou » between them, and no parentheses
 *
 * The form of the filters everybody knows (§8.4). A group is a conjunction, the
 * groups are a disjunction, and the nesting stops there: an editor that allowed
 * parentheses would need a reading order nobody can see on screen.
 *
 * ## An action's kind REPLACES the action
 *
 * The seven kinds are a discriminated union and three of them carry a
 * parameter. `setActionKind` mints a fresh action with a target that exists -
 * the first other step, the first objective of the protocol - because a select
 * that opened on nothing would write a dangling reference the moment it was
 * chosen. `normalizeActions` runs after every change, so the terminal one is
 * always last and always alone.
 */

export interface ExitConditionsProps {
  step: ProtocolStepDraft;
  /** The whole draft: a trigger reaches back, a jump reaches anywhere. */
  steps: readonly ProtocolStepDraft[];
  criteria: readonly CriterionCard[];
  readOnly: boolean;
  onChangeConditions: (next: readonly ExitCondition[]) => void;
}

/** The direction of an action - one field per action, so the mark is local. */
const ACTION_DIRECTION_TESTID = 'protocole-action-direction';

/** The envelope of a select of a condition: the package select fills it. */
const SELECT_BOX = 'min-w-40 max-w-full';

const KEYWORD = 'font-bold text-gray-700 text-caption uppercase tracking-wide';

function toActionKind(value: string): ExitActionKind | null {
  return ACTION_KINDS.find((kind) => kind === value) ?? null;
}

export function ExitConditions({
  step,
  steps,
  criteria,
  readOnly,
  onChangeConditions,
}: ExitConditionsProps) {
  const [drag, setDrag] = useState<{ id: string; overId: string | null } | null>(null);

  const conditions = step.conditions;
  const duplicationBlocked = conditionDuplicationBlocked(conditions);
  const others = otherSteps(steps, step.id);
  const reachable = availableTriggerObjectives(steps, step.id);
  const everyObjective = allObjectives(steps);
  const here = steps.findIndex((one) => one.id === step.id);
  const context: ActionContext = {
    otherStepIds: others.map((one) => one.id),
    objectiveIds: everyObjective.map((place) => place.objective.id),
  };

  const change = (next: ExitCondition) => onChangeConditions(replaceCondition(conditions, next));

  return (
    <div>
      <div className="flex flex-col gap-3">
        {conditions.map((condition, index) => (
          <div
            key={condition.id}
            data-testid={PROTOCOL_TEST_IDS.condition}
            data-condition-id={condition.id}
            draggable={!readOnly}
            onDragStart={() => setDrag({ id: condition.id, overId: null })}
            onDragOver={(event: ReactDragEvent<HTMLElement>) => {
              if (drag === null) return;
              event.preventDefault();
              setDrag((was) => (was === null ? null : { ...was, overId: condition.id }));
            }}
            onDrop={(event: ReactDragEvent<HTMLElement>) => {
              event.preventDefault();
              if (drag === null) return;
              const from = conditions.findIndex((one) => one.id === drag.id);
              const to = index;
              setDrag(null);
              if (from < 0 || from === to) return;
              onChangeConditions(moveCondition(conditions, from, to));
            }}
            onDragEnd={() => setDrag(null)}
            className={`relative rounded-md border-2 border-gray-200 bg-white p-4 pr-12 ${
              drag?.id === condition.id ? 'opacity-40' : ''
            }`}
          >
            <div className="flex gap-3">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-bleu-nuit-tint font-bold text-bleu-nuit text-caption">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="flex flex-wrap items-center gap-2 text-small">
                  <span className={KEYWORD}>quand</span>
                  <span className="text-caption text-gray-600">
                    {condition.groups.length > 1
                      ? 'l’un de ces groupes est vrai · dans un groupe, tout doit être vrai'
                      : 'tout doit être vrai'}
                  </span>
                </p>

                {/* The key is the condition's id plus a RANK, for the groups,
                    the triggers and the actions alike: none of the three
                    carries an id of its own - each one IS its rank inside the
                    condition - and keying on the content would remount the
                    select an operator is typing in. */}
                {condition.groups.map((group, groupIndex) => (
                  <div key={`${condition.id}-g${groupIndex}`}>
                    {groupIndex === 0 ? null : (
                      <p className="my-1.5 flex items-center gap-2.5 font-bold text-caption text-gray-500">
                        <span aria-hidden="true" className="h-px flex-1 bg-rail" />
                        ou
                        <span aria-hidden="true" className="h-px flex-1 bg-rail" />
                      </p>
                    )}
                    <div className="flex flex-col gap-2 border-gray-300 border-l-2 py-1 pl-3">
                      {group.map((trigger, triggerIndex) => (
                        <div
                          key={`${condition.id}-g${groupIndex}-t${triggerIndex}`}
                          className="flex flex-wrap items-center gap-2"
                        >
                          {triggerIndex === 0 ? null : <span className={KEYWORD}>et</span>}
                          {/* The package select has no groups: the objectives, the types
                              and the counters follow each other in the order the three
                              groups « Un objectif », « Un type d’objectif » and « Un
                              compteur » gave them. */}
                          <div data-testid={PROTOCOL_TEST_IDS.trigger} className={SELECT_BOX}>
                            <Select
                              label="Déclencheur"
                              hideLabel
                              compact
                              value={triggerValue(trigger)}
                              disabled={readOnly}
                              onValueChange={(value) =>
                                change(setTrigger(condition, groupIndex, triggerIndex, value))
                              }
                              options={[
                                ...reachable.map((place) => ({
                                  value: `obj:${place.objective.id}`,
                                  label: `${objectiveTitle(place.objective.name, 0)}${
                                    place.stepIndex === here
                                      ? ''
                                      : ` · étape ${place.stepIndex + 1}`
                                  }`,
                                })),
                                ...Object.entries(OBJECTIVE_CLASS_LABEL).map(([which, label]) => ({
                                  value: `type:${which}`,
                                  label,
                                })),
                                ...Object.entries(COUNTER_TRIGGER_LABEL).map(([kind, label]) => ({
                                  value: kind,
                                  label,
                                })),
                              ]}
                            />
                          </div>
                          {trigger.kind === 'objective-status' ? (
                            <div
                              data-testid={PROTOCOL_TEST_IDS.triggerOutcome}
                              className={SELECT_BOX}
                            >
                              <Select
                                label="Statut"
                                hideLabel
                                compact
                                value={trigger.to}
                                disabled={readOnly}
                                onValueChange={(value) => {
                                  const to = objectiveOutcomeSchema.safeParse(value);
                                  if (!to.success) return;
                                  change(
                                    setTriggerOutcome(condition, groupIndex, triggerIndex, to.data),
                                  );
                                }}
                                options={[
                                  { value: 'succeeded', label: 'devient réussi' },
                                  { value: 'failed', label: 'devient raté' },
                                ]}
                              />
                            </div>
                          ) : null}
                          {readOnly ? null : (
                            <IconButton
                              label="Retirer ce déclencheur"
                              onClick={() =>
                                change(removeTrigger(condition, groupIndex, triggerIndex))
                              }
                            />
                          )}
                        </div>
                      ))}
                      {readOnly ? null : (
                        <AddLine
                          testId={PROTOCOL_TEST_IDS.addTrigger}
                          label="et"
                          onClick={() => change(addTrigger(condition, groupIndex))}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {readOnly ? null : (
                  <AddLine
                    testId={PROTOCOL_TEST_IDS.addGroup}
                    label="ou · nouveau groupe"
                    onClick={() => change(addGroup(condition))}
                  />
                )}

                <div className="flex flex-col gap-2 pt-1">
                  {condition.actions.map((action, actionIndex) => (
                    <div
                      key={`${condition.id}-a${actionIndex}`}
                      className="flex flex-wrap items-center gap-2 text-small"
                    >
                      <span className={actionIndex === 0 ? KEYWORD : `${KEYWORD} text-gray-500`}>
                        {actionIndex === 0 ? '→' : 'puis'}
                      </span>
                      <div data-testid={PROTOCOL_TEST_IDS.action} className={SELECT_BOX}>
                        <Select
                          label="Action"
                          hideLabel
                          compact
                          value={action.kind}
                          disabled={readOnly}
                          onValueChange={(value) => {
                            const kind = toActionKind(value);
                            if (kind === null) return;
                            change(setActionKind(condition, actionIndex, kind, context));
                          }}
                          options={ACTION_KINDS.map((kind) => ({
                            value: kind,
                            label: ACTION_LABEL[kind],
                          }))}
                        />
                      </div>
                      <ActionParams
                        action={action}
                        condition={condition}
                        actionIndex={actionIndex}
                        steps={steps}
                        stepId={step.id}
                        criteria={criteria}
                        readOnly={readOnly}
                        onChange={change}
                      />
                      {isTerminalAction(action) && condition.actions.length > 1 ? (
                        <span className="text-caption text-gray-600">
                          action terminale, toujours en dernier
                        </span>
                      ) : null}
                      {readOnly ? null : (
                        <IconButton
                          label="Retirer l’action"
                          onClick={() => change(removeAction(condition, actionIndex))}
                        />
                      )}
                    </div>
                  ))}
                  {readOnly ? null : (
                    <AddLine
                      testId={PROTOCOL_TEST_IDS.addAction}
                      label={
                        condition.actions.some(isTerminalAction)
                          ? 'action non terminale, avant la dernière'
                          : 'action'
                      }
                      onClick={() => change(addAction(condition, context))}
                    />
                  )}
                </div>
              </div>
            </div>

            {readOnly ? null : (
              <div className="absolute top-3 right-3 grid justify-items-center gap-1">
                <Tooltip content="Glisser pour changer la priorité">
                  <span className="grid cursor-grab p-1">
                    <span className="inline-flex text-gray-400">
                      <Icon name="drag_indicator" size={16} />
                    </span>
                  </span>
                </Tooltip>
                {/* The copy icon, just before the cross (§4.2). */}
                <DuplicateButton
                  testId={PROTOCOL_TEST_IDS.duplicateCondition}
                  label={`Dupliquer la condition de sortie ${index + 1}`}
                  blocked={duplicationBlocked}
                  shape="compact"
                  onClick={() => {
                    const done = duplicateCondition(conditions, condition.id);
                    if (done !== null) onChangeConditions(done.list);
                  }}
                />
                <IconButton
                  testId={PROTOCOL_TEST_IDS.removeCondition}
                  label="Supprimer la condition de sortie"
                  onClick={() => onChangeConditions(removeCondition(conditions, condition.id))}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3.5 text-caption text-gray-600">
        {readOnly ? null : (
          <button
            type="button"
            data-testid={PROTOCOL_TEST_IDS.addCondition}
            disabled={conditions.length >= PROTOCOL_CONDITIONS_MAX}
            onClick={() => onChangeConditions([...conditions, newCondition()])}
            className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md px-3.5 font-semibold text-bleu-nuit text-small hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
          >
            <Icon name="add" size={16} />
            Ajouter une condition de sortie
          </button>
        )}
        <span>sans condition, la logique implicite s’applique seule</span>
      </div>
    </div>
  );
}

/** The one parameter an action carries, drawn beside its kind (§8.4). */
function ActionParams({
  action,
  condition,
  actionIndex,
  steps,
  stepId,
  criteria,
  readOnly,
  onChange,
}: {
  action: ExitAction;
  condition: ExitCondition;
  actionIndex: number;
  steps: readonly ProtocolStepDraft[];
  stepId: string;
  criteria: readonly CriterionCard[];
  readOnly: boolean;
  onChange: (next: ExitCondition) => void;
}) {
  if (action.kind === 'direction') {
    return (
      <span className="min-w-60 flex-1">
        <DirectionField
          value={action.text}
          onChange={(text) =>
            onChange(setActionParam(condition, actionIndex, { field: 'text', value: text }))
          }
          readOnly={readOnly}
          placeholder="La consigne servie à l’interviewer au tour suivant."
          ariaLabel="Direction d’IA"
          testId={ACTION_DIRECTION_TESTID}
          insertTestId={null}
          steps={steps}
          stepId={stepId}
          criteria={criteria}
          minHeightClass="min-h-16"
        />
      </span>
    );
  }

  if (action.kind === 'go-to-step') {
    const others = otherSteps(steps, stepId);
    return (
      <div className={SELECT_BOX}>
        <Select
          label="Étape visée"
          hideLabel
          compact
          value={action.stepId}
          disabled={readOnly}
          onValueChange={(value) =>
            onChange(setActionParam(condition, actionIndex, { field: 'stepId', value }))
          }
          options={others.map((one) => ({
            value: one.id,
            label: stepTitle({ index: steps.indexOf(one), name: one.name }),
          }))}
        />
      </div>
    );
  }

  if (action.kind === 'invalidate-objective') {
    return (
      <div className={SELECT_BOX}>
        <Select
          label="Objectif à invalider"
          hideLabel
          compact
          value={action.objectiveId}
          disabled={readOnly}
          onValueChange={(value) =>
            onChange(setActionParam(condition, actionIndex, { field: 'objectiveId', value }))
          }
          options={allObjectives(steps).map((place) => ({
            value: place.objective.id,
            label: `${objectiveTitle(place.objective.name, 0)} · étape ${place.stepIndex + 1}`,
          }))}
        />
      </div>
    );
  }

  if (action.kind === 'close-experience') {
    const isDefault = action.text === PROTOCOL_DEFAULT_CLOSE_TEXT;
    return (
      <>
        <div className="min-w-60 flex-1">
          <TextField
            label="Texte de clôture"
            hideLabel
            compact
            value={action.text}
            readOnly={readOnly}
            maxLength={PROTOCOL_CLOSE_TEXT_MAX}
            onChange={(event) =>
              onChange(
                setActionParam(condition, actionIndex, {
                  field: 'text',
                  value: event.currentTarget.value,
                }),
              )
            }
          />
        </div>
        {isDefault || readOnly ? (
          <span className="text-caption text-gray-600">texte par défaut</span>
        ) : (
          <button
            type="button"
            onClick={() =>
              onChange(
                setActionParam(condition, actionIndex, {
                  field: 'text',
                  value: PROTOCOL_DEFAULT_CLOSE_TEXT,
                }),
              )
            }
            className="cursor-pointer text-bleu-nuit text-caption underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            remettre le texte par défaut
          </button>
        )}
      </>
    );
  }

  return null;
}

/** « + et », « + ou · nouveau groupe », « + action » - one shape for the three. */
function AddLine({
  testId,
  label,
  onClick,
}: {
  testId: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2.5 font-semibold text-bleu-nuit text-caption hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        <Icon name="add" size={14} />
        {label}
      </button>
    </div>
  );
}

/** A cross in a gutter: the one remove gesture of every line of a condition. */
function IconButton({
  testId,
  label,
  onClick,
}: {
  testId?: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
    >
      <Icon name="close" size={14} />
    </button>
  );
}
